import { useTheme } from '@emotion/react'
import {
    AppState,
    createEditionSelectItemEvent,
    entitledToViewContent,
    Issue,
    PremiumPublicationsSet,
} from '@news-mono/web-common'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useSection } from '../../__helpers'
import { useProduct } from '../../__product'
import { calcRem, colors } from '../../__styling'
import {
    StyledEPaperArrow,
    StyledEPaperBanner,
    StyledEPaperBannerWrapper,
    StyledEPaperButton,
    StyledEPaperChevon,
    StyledEPaperCoffee,
    StyledEPaperCoverLeft,
    StyledEPaperCoverRight,
    StyledEPaperDateTime,
    StyledEPaperHeading,
    StyledEPaperLink,
    StyledEPaperPNButton,
    StyledEPaperSubheading,
    StyledEpaperSubscribeButton,
    StyledEpaperSubscribeText,
    StyledEpaperSubscribeUnderlineText,
    StyledEpaperUnsubscribedBanner,
} from './ePaperPage.styled'

export type PNRegionalSections =
    | 'joondalup'
    | 'wanneroo'
    | 'stirling'
    | 'western-suburbs'
    | 'central'
    | 'southern'
    | 'canning'
    | 'cockburn'
    | 'fremantle'
    | 'melville'
    | 'mandurah-times'
    | 'northern-suburbs'

interface ePaperPageProps {
    latestEdition: Issue
    allEditions: Issue[]
    onEvent: (event: any) => void
    regionalSection?: PNRegionalSections
    title: string
}

/** Returns the appropriate background image and button colouring for the given publication */
const getImageAndColourForPNPublication = (
    publication: PNRegionalSections | undefined,
): {
    image: string
    color: string
} => {
    switch (publication) {
        case 'central':
        case 'southern': {
            return {
                image: 'central',
                color: colors.perthnow.purpleAmethyst,
            }
        }
        case 'northern-suburbs': {
            return {
                image: 'northern-suburbs',
                color: colors.perthnow.northernSuburbsBlue,
            }
        }
        case 'western-suburbs': {
            return {
                image: 'western-suburbs',
                color: colors.perthnow.westernSuburbsGreen,
            }
        }
        case 'canning':
        case 'cockburn':
        case 'fremantle':
        case 'melville': {
            return {
                image: 'south',
                color: colors.perthnow.blueCerulean,
            }
        }
        case 'joondalup':
        case 'wanneroo':
        case 'stirling': {
            return {
                image: 'north',
                color: colors.perthnow.pinkThulite,
            }
        }
        case 'mandurah-times': {
            return {
                image: 'mandurah-times',
                color: colors.black,
            }
        }
        default: {
            return { image: 'default', color: 'default' }
        }
    }
}

export const EPaperPage: React.FC<ePaperPageProps> = ({
    regionalSection,
    title,
    latestEdition,
    onEvent,
}) => {
    const isEntitled = entitledToViewContent(
        {
            level: 'subscriber',
        },
        useSelector((state: AppState) => state.authentication),
    )

    const section = useSection()
    const product = useProduct()
    const theme = useTheme()
    const sectionInfo = getImageAndColourForPNPublication(regionalSection)
    const today = new Date()

    const [originUrl, setOriginUrl] = useState('')

    // Build heading text.
    let headingText = "Today's Digital Edition"
    if (product === 'thewest') {
        if (section !== 'default') {
            headingText = `${title} Latest Digital Edition`
        }
    } else if (product === 'perthnow') {
        if (
            ['western suburbs', 'northern suburbs'].includes(
                title.toLowerCase(),
            )
        ) {
            headingText = `${title} Weekly Digital Edition`
        } else {
            headingText = `${title} Digital Edition`
        }
    }

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setOriginUrl(window.location.origin)
        }
    }, [])
    return (
        <>
            {theme.kind === 'perthnow' && (
                <StyledEPaperLink to={`${originUrl}/editions`} isPrimary={true}>
                    <StyledEPaperArrow />
                    All Editions
                </StyledEPaperLink>
            )}
            <StyledEPaperHeading>{headingText}</StyledEPaperHeading>
            {theme.kind === 'perthnow' && (
                <div style={{ paddingBottom: calcRem(16) }}>
                    Printed weekly on{' '}
                    {regionalSection === 'mandurah-times'
                        ? ' Wednesday'
                        : ' Thursday'}
                </div>
            )}
            {!isEntitled &&
            PremiumPublicationsSet[latestEdition.publicationName] ? (
                <StyledEpaperUnsubscribedBanner
                    bannerUrls={{
                        mobile: require('./assets/unsubscribed/img-epaper-bg-UNSUB-S.webp'),
                        tablet: require('./assets/unsubscribed/img-epaper-bg-UNSUB-M.webp'),
                        desktop: require('./assets/unsubscribed/img-epaper-bg-UNSUB-L.webp'),
                    }}
                    backgroundGradient={
                        'linear-gradient(91deg, rgba(0, 12, 35, 0.8) 0.63%, rgba(0, 0, 0, 0) 126.65%)'
                    }
                >
                    <StyledEpaperSubscribeText>
                        Read the paper, as it was printed
                    </StyledEpaperSubscribeText>
                    <StyledEpaperSubscribeUnderlineText>
                        $8 per week
                    </StyledEpaperSubscribeUnderlineText>
                    <StyledEpaperSubscribeButton to={`/editions/subscribe`}>
                        Subscribe now
                        <StyledEPaperChevon />
                    </StyledEpaperSubscribeButton>
                </StyledEpaperUnsubscribedBanner>
            ) : (
                <StyledEPaperBanner section={sectionInfo.image}>
                    <StyledEPaperBannerWrapper>
                        <StyledEPaperDateTime id={'timestamp-read-now'}>
                            {product === 'thewest' &&
                            section != 'default' &&
                            latestEdition.date.getDate() - today.getDate() === 0
                                ? "Today's paper"
                                : latestEdition?.date?.toLocaleDateString(
                                      'en-AU',
                                      {
                                          weekday: 'long',
                                          day: 'numeric',
                                          month: 'short',
                                      },
                                  )}
                        </StyledEPaperDateTime>
                        <StyledEPaperSubheading>
                            As it was printed
                        </StyledEPaperSubheading>
                        {theme.kind === 'perthnow' && (
                            <StyledEPaperPNButton
                                to={latestEdition?.url}
                                color={sectionInfo.color}
                                onClick={() =>
                                    onEvent(
                                        createEditionSelectItemEvent({
                                            issue: latestEdition,
                                            collectionName: `PerthNowEditionPage.${regionalSection}`,
                                            product,
                                            itemDetails: {
                                                category: `perthnow_${latestEdition.publicationName}_editions_page`,
                                                linkText: 'Read now',
                                                linkUrl: latestEdition.url,
                                                itemName: `${
                                                    latestEdition.publicationName
                                                } - ${
                                                    latestEdition.date.toLocaleString() ??
                                                    ''
                                                }`,
                                            },
                                        }),
                                    )
                                }
                            >
                                Read now{' '}
                                <StyledEPaperChevon aria-hidden={true} />
                            </StyledEPaperPNButton>
                        )}
                        {theme.kind === 'thewest' && (
                            <StyledEPaperButton
                                to={latestEdition?.url}
                                onClick={() =>
                                    onEvent(
                                        createEditionSelectItemEvent({
                                            issue: latestEdition,
                                            collectionName: `TheWestEditionPage.${regionalSection}`,
                                            product,
                                            itemDetails: {
                                                category: `thewest_${latestEdition.publicationName}_editions_page`,
                                                linkText: 'Read now',
                                                linkUrl: latestEdition.url,
                                            },
                                        }),
                                    )
                                }
                            >
                                Read now{' '}
                                <span aria-hidden={true}>
                                    <StyledEPaperChevon />
                                </span>
                            </StyledEPaperButton>
                        )}
                    </StyledEPaperBannerWrapper>
                    <StyledEPaperCoverLeft
                        src={latestEdition?.coverUrl}
                        draggable={false}
                        aria-hidden={true}
                        alt={''}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null
                            currentTarget.src = require('./assets/paper.jpg')
                        }}
                    />
                    <StyledEPaperCoverRight
                        draggable={false}
                        src={latestEdition?.coverUrl}
                        aria-hidden={true}
                        alt={''}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null
                            currentTarget.src = require('./assets/paper.jpg')
                        }}
                    />
                    {theme.kind === 'thewest' && (
                        <>
                            <StyledEPaperCoffee
                                src={require('./assets/coffee.png')}
                                draggable={false}
                                aria-hidden={true}
                                alt={''}
                            />
                        </>
                    )}
                </StyledEPaperBanner>
            )}
        </>
    )
}

EPaperPage.displayName = 'ePaperPage'

export default EPaperPage
